import Controller from 'controllers/controller';
import Elements from 'tools/elements';
import EventManager from 'tools/event-manager';
import Animator from 'tools/animator';
import Helpers from 'tools/helpers';
import {Power2} from 'tools/gsap';

class TabsController extends Controller {
    ready(container, options = { }) {
        this.container = container;
        
        this.options = this.resolveOptions(options);
        
        this.elements = new Elements(container);
        this.events = new EventManager;
        this.animator = new Animator({
            elements: this.elements.elements,
            duration: 0.75,
            ease: Power2.easeInOut
        });
        
        this.animating = false;
        
        this.tabs = [ ];
        this.tabsByName = { };
        this.tabsByPath = { };
        this.currentTab = null;
        
        this.cacheElements();
        this.cacheTabs();
        this.attachEvents();

        this.setInitialActiveTab();
    }

    /**
     * Resolve the final options considering JSON-encoded options on the container element.
     * 
     * @param options
     * @return {object}
     */
    resolveOptions(options = { }) {
        // Merge user options with default options
        options = Object.assign({
            history: false // If true, changing tabs will change the URL of the page
        }, options);
        
        // Allow options to be set on the element
        let containerOptions = this.container.getAttribute('data-options') || '{ }';
        
        try {
            containerOptions = JSON.parse(containerOptions);
        } catch (error) {
            console.log(this.container);
            
            throw new Error('Invalid/malformed options specified in data-options property of logged element');
        }
        
        return Object.assign(options, containerOptions);
    }
    
    cacheElements() {
        this.elements.queryAll('tab-button', '.tab-button');
        this.elements.queryAll('tab-buttons', '.tab-buttons');
        this.elements.queryAll('tab-pane', '.tab-pane');
        this.elements.queryAll('tab-panes', '.tab-panes');
    }

    cacheTabs() {
        this.elements.each('tab-button', (button, index) => {
            let model = this.generateTabModel(button, index);
            
            console.log(model);
            
            this.tabs.push(model);
            this.tabsByName[model.name] = model;
            this.tabsByPath[model.path] = model;
        });
    }

    setInitialActiveTab() {
        let active = null;
        
        this.elements.each('tab-button', button => {
            if (button.classList.contains('active')) {
                active = button;
            }
        });
        
        if ( ! active) {
            let buttons = this.elements.get('tab-button');
            
            active = buttons && buttons[0] || null;
        }
        
        this.currentTab = this.getTabModel(active);
    }

    /**
     * Generates a tab model, given the tab button and it's index.
     * 
     * @param button
     * @param index
     * @return {*}
     */
    generateTabModel(button, index) {
        let name = button.getAttribute('data-name'),
            path = button.getAttribute('href');
        
        return {
            name,
            path,
            button,
            pane: this.elements.get('tab-pane')[index]
        }
    }

    /**
     * Get tab model by button
     * 
     * @param button - The tab button
     * @return {*|null}
     */
    getTabModel(button) {
        let name = button.getAttribute('data-name');

        return this.tabsByName[name] || null;
    }

    attachEvents() {
        this.elements.each('tab-button', button => {
            this.events.on(button, 'click', this.onTabButtonClick);
        })
    }
    
    beforeDestroy() {
        this.events.destroy();
        this.app.historyController.bus.off('change', this.onHistoryChange);
    }

    onHistoryChange(state) {
        if (this.options.history) {
            let model = this.tabsByPath[state.location.pathname];

            if (model) {
                this.openTab(model);
            }
        }
    }

    onTabButtonClick(event) {
        event.preventDefault();
        
        // If we're in the middle of a tab change, don't change it again. Ignore it
        if (this.animating) {
            return;
        }
        
        let model = this.getTabModel(event.currentTarget);

        if (this.options.history) {
            this.app.historyController.push(model.path);
        }
        
        this.openTab(model);
    }

    /**
     * Given a tab model, set the active tab.
     * 
     * @param model
     */
    setActiveTab(model) {
        this.tabs.forEach(tab => {
            tab.button.classList.remove('active');
            
            if (model === tab) {
                tab.button.classList.add('active');
            }
        })
    }

    /**
     * Given a tab model, set the active pane.
     * 
     * @param model
     */
    setActivePane(model) {
        this.tabs.forEach(tab => {
            tab.pane.classList.remove('active');

            if (model === tab) {
                tab.pane.classList.add('active');
            }
        })
    }

    /**
     * Get the direction of the animation given the new tab.
     *
     * @param newModel
     * @param oldModel
     * @return {number}
     */
    getAnimationDirection(newModel, oldModel) {
        let newIndex = this.tabs.indexOf(newModel),
            oldIndex = this.tabs.indexOf(oldModel);

        return Math.sign(newIndex - oldIndex);
    }
    
    async animatePanes(model, oldModel) {
        // Animate in the direction of the clicked tab
        let direction = this.getAnimationDirection(model, oldModel),
            amount = 50;

        this.animating = true;

        // Set next pane 20px to the right and 0 opacity and display block
        this.animator.set(model.pane, {
            opacity: 0,
            display: 'block',
            x: direction * amount // multiply direction by value so that panes animate in the correct direction
        });

        // Get height of current panes for animating below
        let nextTabHeight = model.pane.offsetHeight,
            currentTabHeight = oldModel.pane.offsetHeight;

        // Set tabs height to that of current pane
        this.animator.set('tab-panes', {
            height: currentTabHeight,
            position: 'relative',
            // Hide overflow so tab height animation looks good
            overflow: 'hidden'
        });

        // Set all panes to absolute, so they overlap
        this.animator.set('tab-pane', {
            position: 'absolute',
            top: 0,
            left: 0
        });

        await Helpers.sleep(20);

        // Animate old pane out of view
        this.animator.animate(oldModel.pane, {
            x: direction * -amount,
            opacity: 0
        });

        // Animate height of tabs to that of next tab pane
        this.animator.animate(this.elements.get('tab-panes'), {
            height: nextTabHeight
        });

        // Animate new pane into view
        await this.animator.animate(model.pane, {
            x: 0,
            opacity: 1
        });

        await Helpers.sleep(20);

        // Set all panes to absolute, so they overlap
        this.animator.set('tab-pane', {
            position: '',
            top: '',
            left: '',
            display: ''
        });

        // Set tabs height to that of current pane
        this.animator.set('tab-panes', {
            height: 'auto',
            position: 'relative',
            // Hide overflow so tab height animation looks good
            overflow: 'hidden'
        });
    }

    /**
     * Given a tab model, will open the given tab.
     * 
     * Will return 1 if the tab is after the current, -1 if the tab is before the current, or 0 if the tab is the current.
     * 
     * @param model
     * @return {Promise<void>}
     */
    async openTab(model) {
        // If tab is same, ignore. If tab still animating, ignore.
        if (model === this.currentTab || this.animating) {
            return;
        }
        
        let oldTab = this.currentTab;
        
        this.currentTab = model;
        
        // Set the active tab now
        this.setActiveTab(model);
        
        await this.animatePanes(model, oldTab);
        
        // Set the active pane independently 
        this.setActivePane(model);
        
        this.animating = false;
    }
}

export default TabsController;


// WEBPACK FOOTER //
// ./site/templates/resources/src/js/controllers/components/tabs.js